import React, { useMemo } from 'react';
import { AddUniversal, TrashCanOutlined } from '@duxton-web/icons';
import { Button, Card, Divider, Tag } from '@duxton-web/ui';

import { DEFAULT_CONSTRAINT } from '@/common/ConstraintsEditor';
import ConstraintEditor from '@/common/ConstraintsEditor/Constraint';
import { useVariableContext } from '@/features/variable/contexts/variable-context';
import ValidationContent from '@/features/variable/settings/validation/contextual/validation-content';
import Service from '@/lib/App';
import { Constraint, ConstraintType, ContextualRule } from '@/lib/PlannerDTOs';

import './index.css';

export const MAX_CONSTRAINTS = 4;

export const DEFAULT_RULE_CONDITIONS: Constraint[] = [
  { ...DEFAULT_CONSTRAINT },
];

interface RuleCardProps {
  title: string;
  ruleIndex: number;
  rule: ContextualRule;
  disabled?: boolean;
  onDelete: () => void;
  onRuleChange: (ruleIndex: number, rule: ContextualRule) => void;
}

const RuleCard = ({
  title,
  ruleIndex,
  rule,
  disabled,
  onDelete,
  onRuleChange,
}: RuleCardProps) => {
  const { variable } = useVariableContext();
  const allowedFacets = useMemo(() => {
    const mandatoryFacets = variable?.metadata?.mandatoryFacets ?? [];
    const variableFacets = variable?.facets ?? [];
    const seen = new Set<string>();
    return Service.cache
      .getAllFacets()
      .filter(
        (facet) =>
          mandatoryFacets.includes(facet.name) ||
          variableFacets.includes(facet.name),
      )
      .sort((a, b) => {
        const aIsMandatory = mandatoryFacets.includes(a.name);
        const bIsMandatory = mandatoryFacets.includes(b.name);
        if (aIsMandatory && !bIsMandatory) return -1;
        if (!aIsMandatory && bIsMandatory) return 1;
        return 0;
      })
      .filter((facet) => {
        if (seen.has(facet.name)) return false;
        seen.add(facet.name);
        return true;
      })
      .map((facet) => ({ value: facet.name, label: facet.title }));
  }, [variable]);

  const constraints = useMemo(() => {
    if (!rule?.contextualRulesConditions?.length) {
      return DEFAULT_RULE_CONDITIONS.map((condition) => ({ ...condition }));
    }

    return rule.contextualRulesConditions.map((condition) => ({
      ...condition,
      type: ConstraintType.Standard,
    }));
  }, [rule?.contextualRulesConditions]);

  const toNextRule = (nextConstraints: Constraint[]) => {
    return {
      ...rule,
      contextualRulesConditions: nextConstraints.map((condition) => ({
        target: condition?.target || '',
        op: condition?.op || '',
        value: String(condition?.value || ''),
      })),
    };
  };

  const handleConditionUpdate = (index: number, condition: Constraint) => {
    const nextConditions = [...constraints];
    nextConditions[index] = condition;
    onRuleChange(ruleIndex, toNextRule(nextConditions));
  };

  const handleAddCondition = (index: number) => {
    const nextConditions = [...constraints];
    nextConditions.splice(index + 1, 0, { ...DEFAULT_CONSTRAINT });
    onRuleChange(ruleIndex, toNextRule(nextConditions));
  };

  const handleDeleteCondition = (index: number) => {
    if (constraints.length <= 1) return;

    const nextConditions = [...constraints];
    nextConditions.splice(index, 1);
    onRuleChange(ruleIndex, toNextRule(nextConditions));
  };

  return (
    <Card
      size="small"
      title={title}
      extra={
        <Button
          size="small"
          type="text"
          disabled={disabled}
          onClick={onDelete}
          icon={<TrashCanOutlined />}
        />
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {constraints.map((constraint, index) => {
          const showDeleteButton = index > 0 || constraints.length > 1;
          return (
            <div
              key={`rule-condition-${index}`}
              style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}
            >
              <Tag
                size="small"
                style={{
                  marginInlineEnd: 0,
                  height: 40,
                  width: 56,
                  fontWeight: 600,
                  lineHeight: '34px',
                  textAlign: 'center',
                  fontSize: 12,
                }}
              >
                {index === 0 ? 'WHEN' : 'AND'}
              </Tag>
              <ConstraintEditor
                index={index}
                disabled={disabled}
                constraint={constraint}
                constraints={constraints}
                isCustomConstraint={constraint.type !== ConstraintType.Standard}
                allowedFacets={allowedFacets}
                onChange={handleConditionUpdate}
                rolloutVariable={variable}
                showFacetStatus={false}
              />
              <div style={{ display: 'flex', gap: 8, alignSelf: 'flex-end' }}>
                {showDeleteButton && (
                  <Button
                    shape="default"
                    disabled={disabled}
                    icon={<TrashCanOutlined />}
                    onClick={() => handleDeleteCondition(index)}
                  />
                )}
              </div>
            </div>
          );
        })}
        <Button
          className="add-condition-button"
          disabled={disabled || constraints.length >= MAX_CONSTRAINTS}
          icon={<AddUniversal />}
          shape="default"
          style={{ width: 'fit-content' }}
          onClick={() => handleAddCondition(constraints.length - 1)}
        >
          Add Condition
        </Button>
        <Divider style={{ margin: 0 }} />
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <Tag
            size="small"
            style={{
              marginInlineEnd: 0,
              height: 40,
              width: 56,
              fontWeight: 600,
              lineHeight: '34px',
              textAlign: 'center',
              fontSize: 12,
            }}
          >
            THEN
          </Tag>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <ValidationContent
              rule={rule}
              disabled={disabled}
              onRuleChange={(nextRule) => onRuleChange(ruleIndex, nextRule)}
            />
          </div>
        </div>
      </div>
    </Card>
  );
};

export default RuleCard;
